import { InfiniteMovingCards } from "@designs-by-maryam/ui/components/infinite-moving-cards";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { useMemo } from "react";
import { type Collaboration, collaborations } from "@/data/collaborations";
import { useSafeMotion } from "@/lib/motion";
import { featuredTestimonialsQueryOptions } from "@/lib/query-options";

type CollaborationsCarouselProps = {
	className?: string;
	direction?: "left" | "right";
	speed?: "fast" | "normal" | "slow";
	pauseOnHover?: boolean;
};

/**
 * CollaborationsCarousel Component
 *
 * Displays client/partner logos in an infinite scrolling carousel.
 * On hover, if a testimonial quote exists for that client, it reveals the quote.
 *
 * Features:
 * - Infinite auto-scrolling animation
 * - Pause on hover (WCAG accessibility)
 * - Logo-to-quote reveal on hover
 * - Responsive card sizing
 * - Motion entrance animation
 */
export function CollaborationsCarousel({
	className,
	direction = "left",
	speed = "fast",
	pauseOnHover = true,
}: CollaborationsCarouselProps) {
	const { shouldReduce, fadeIn, viewportOnce } = useSafeMotion();

	// Fetch testimonials from CMS with static fallback
	const { data, isError } = useQuery({
		...featuredTestimonialsQueryOptions(),
		retry: 1,
		staleTime: 60 * 60 * 1000, // 1 hour
	});

	// Map CMS testimonials to carousel item format, fallback to static data
	const carouselItems = useMemo<Collaboration[]>(() => {
		// Use static fallback if error or no testimonials
		if (isError || !data?.data?.length) {
			return collaborations;
		}

		// Map testimonials to carousel format
		return data.data.map((t) => ({
			name: t.company_name ?? t.client_name,
			logo: t.logo_url ?? "", // Use deprecated logo_url field (or empty for placeholder)
			quote: t.content,
			title: t.client_role ?? undefined,
		}));
	}, [data, isError]);

	return (
		<section
			aria-label="Trusted Partnerships"
			className={cn("relative py-6 md:py-8", className)}
			data-testid="collaborations-section"
		>
			<div className="container relative mx-auto px-4">
				{/* Logo Carousel with gradient fade edges */}
				<motion.div
					className="relative"
					initial={fadeIn.initial}
					transition={{ ...fadeIn.transition, delay: shouldReduce ? 0 : 0.3 }}
					viewport={viewportOnce}
					whileInView={fadeIn.animate}
				>
					{/* Gradient fade overlays on edges */}
					<div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-gradient-to-r from-background to-transparent" />
					<div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-gradient-to-l from-background to-transparent" />

					<InfiniteMovingCards
						className={cn(
							"py-8",
							shouldReduce && "motion-reduce:[animation-play-state:paused]",
						)}
						direction={direction}
						items={carouselItems}
						pauseOnHover={pauseOnHover}
						speed={speed}
					/>
				</motion.div>
			</div>
		</section>
	);
}

export default CollaborationsCarousel;
